/**
 * DailyListTab - Shows today's sign-in list by category
 * Extracted from old HomePage.js for HomePageNew.js integration
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { format } from "date-fns";
import { Users, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DailyListTab = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [todaySignIns, setTodaySignIns] = useState([]);

  useEffect(() => {
    fetchTodaySignIns();
  }, []);

  const fetchTodaySignIns = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const response = await axios.get(`${API}/signins?date=${today}`);
      setTodaySignIns(response.data || []);
    } catch (error) {
      console.error("Error fetching today's sign-ins:", error);
    }
    setLoading(false);
  };

  const handleTodaySignIn = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    navigate(`/signin/${today}`);
  };

  // Full Time - only show those who are signed in (have dropOffTime)
  // Handle both old format ('Full Time', 'regular') and new format ('full_time')
  const fullTimeChildren = todaySignIns
    .filter(s => (s.category === 'full_time' || s.category === 'regular' || s.category === 'Full Time') && s.dropOffTime)
    .sort((a, b) => a.childName.localeCompare(b.childName));

  // School Age - only show signed in (have AM drop off OR PM bus drop off)
  const schoolAgeChildren = todaySignIns
    .filter(s => (s.category === 'school_age' || s.category === 'School Age') && (s.amParentDropOffTime || s.pmSchoolAgeDropOffTime))
    .sort((a, b) => a.childName.localeCompare(b.childName));

  // Part Time children
  const partTimeChildren = todaySignIns
    .filter(s => (s.category === 'part_time' || s.category === 'Part Time') && s.dropOffTime)
    .sort((a, b) => a.childName.localeCompare(b.childName));

  // After School children
  const afterSchoolChildren = todaySignIns
    .filter(s => (s.category === 'after_school' || s.category === 'After School') && s.dropOffTime)
    .sort((a, b) => a.childName.localeCompare(b.childName));

  const hasNoSignIns = fullTimeChildren.length === 0 && 
                        schoolAgeChildren.length === 0 && 
                        partTimeChildren.length === 0 && 
                        afterSchoolChildren.length === 0;

  return (
    <div data-testid="daily-list-view">
      <Card className="bg-white border border-line shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between border-b border-line pb-4">
          <div>
            <CardTitle className="text-xl font-semibold text-ink-main flex items-center gap-2">
              <Users className="w-5 h-5 text-brand-primary" />
              {language === 'es' ? 'Hoja de Registro de Hoy' : "Today's Sign-In Sheet"}
            </CardTitle>
            <p className="text-sm text-ink-muted mt-1">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
          </div>
          <Button
            onClick={handleTodaySignIn}
            className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
            data-testid="open-today-signin-btn"
          >
            {language === 'es' ? 'Abrir Registro' : 'Open Sign-In'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : hasNoSignIns ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-primary/10 mb-4">
                <Users className="w-8 h-8 text-brand-primary" />
              </div>
              <p className="text-ink-muted mb-4">
                {language === 'es' ? 'No hay niños registrados hoy.' : 'No children signed in today.'}
              </p>
              <Button 
                onClick={handleTodaySignIn} 
                className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
              >
                {language === 'es' ? 'Abrir Registro' : 'Open Sign-In'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Full Time Children - AM Sign In / PM Sign Out */}
              {fullTimeChildren.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-brand-primary mb-3 flex items-center gap-2">
                    <span className="bg-brand-primary/10 text-brand-primary px-2 py-1 rounded text-sm font-bold">
                      {fullTimeChildren.length}
                    </span>
                    {language === 'es' ? 'Tiempo Completo' : 'Full Time'}
                  </h3>
                  <div className="bg-surface-bg rounded-xl border border-line overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-brand-primary/10">
                        <tr>
                          <th className="text-left p-3 text-brand-primary font-semibold">
                            {language === 'es' ? 'Nombre' : 'Name'}
                          </th>
                          <th className="text-left p-3 text-brand-primary font-semibold">
                            {language === 'es' ? 'Entrada AM' : 'AM Sign In'}
                          </th>
                          <th className="text-left p-3 text-brand-primary font-semibold">
                            {language === 'es' ? 'Salida PM' : 'PM Sign Out'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {fullTimeChildren.map((signin, idx) => (
                          <tr key={signin.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-surface-bg/50'}>
                            <td className="p-3 font-medium text-ink-main">{signin.childName}</td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.dropOffTime || '-'}</div>
                              {signin.dropOffPerson && <div className="text-xs text-ink-muted/70">{signin.dropOffPerson}</div>}
                            </td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.pickUpTime || '-'}</div>
                              {signin.pickUpPerson && <div className="text-xs text-ink-muted/70">{signin.pickUpPerson}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* School Age Children - AM Signed In / PM Drop Off / Parent Pick Up */}
              {schoolAgeChildren.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-brand-secondary mb-3 flex items-center gap-2">
                    <span className="bg-brand-secondary/10 text-brand-secondary px-2 py-1 rounded text-sm font-bold">
                      {schoolAgeChildren.length}
                    </span>
                    {language === 'es' ? 'Edad Escolar' : 'School Age'}
                  </h3>
                  <div className="bg-surface-bg rounded-xl border border-line overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-brand-secondary/10">
                        <tr>
                          <th className="text-left p-3 text-brand-secondary font-semibold">
                            {language === 'es' ? 'Nombre' : 'Name'}
                          </th>
                          <th className="text-left p-3 text-brand-secondary font-semibold">
                            {language === 'es' ? 'Entrada AM' : 'AM Signed In'}
                          </th>
                          <th className="text-left p-3 text-brand-secondary font-semibold">
                            {language === 'es' ? 'Llegada PM' : 'PM Drop Off'}
                          </th>
                          <th className="text-left p-3 text-brand-secondary font-semibold">
                            {language === 'es' ? 'Salida PM' : 'Parent Pick Up'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {schoolAgeChildren.map((signin, idx) => (
                          <tr key={signin.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-surface-bg/50'}>
                            <td className="p-3 font-medium text-ink-main">{signin.childName}</td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.amParentDropOffTime || '-'}</div>
                              {signin.amDropOffPerson && <div className="text-xs text-ink-muted/70">{signin.amDropOffPerson}</div>}
                            </td>
                            <td className="p-3 text-ink-muted">{signin.pmSchoolAgeDropOffTime || '-'}</td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.pmParentPickupTime || '-'}</div>
                              {signin.pmPickupPerson && <div className="text-xs text-ink-muted/70">{signin.pmPickupPerson}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Part Time Children */}
              {partTimeChildren.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-brand-accent mb-3 flex items-center gap-2">
                    <span className="bg-brand-accent/10 text-brand-accent px-2 py-1 rounded text-sm font-bold">
                      {partTimeChildren.length}
                    </span>
                    {language === 'es' ? 'Medio Tiempo' : 'Part Time'}
                  </h3>
                  <div className="bg-surface-bg rounded-xl border border-line overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-brand-accent/10">
                        <tr>
                          <th className="text-left p-3 text-brand-accent font-semibold">
                            {language === 'es' ? 'Nombre' : 'Name'}
                          </th>
                          <th className="text-left p-3 text-brand-accent font-semibold">
                            {language === 'es' ? 'Entrada' : 'Sign In'}
                          </th>
                          <th className="text-left p-3 text-brand-accent font-semibold">
                            {language === 'es' ? 'Salida' : 'Sign Out'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {partTimeChildren.map((signin, idx) => (
                          <tr key={signin.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-surface-bg/50'}>
                            <td className="p-3 font-medium text-ink-main">{signin.childName}</td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.dropOffTime || '-'}</div>
                              {signin.dropOffPerson && <div className="text-xs text-ink-muted/70">{signin.dropOffPerson}</div>}
                            </td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.pickUpTime || '-'}</div>
                              {signin.pickUpPerson && <div className="text-xs text-ink-muted/70">{signin.pickUpPerson}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* After School Children */}
              {afterSchoolChildren.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-brand-highlight mb-3 flex items-center gap-2">
                    <span className="bg-brand-highlight/10 text-brand-highlight px-2 py-1 rounded text-sm font-bold">
                      {afterSchoolChildren.length}
                    </span>
                    {language === 'es' ? 'Después de Escuela' : 'After School'}
                  </h3>
                  <div className="bg-surface-bg rounded-xl border border-line overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-brand-highlight/10">
                        <tr>
                          <th className="text-left p-3 text-brand-highlight font-semibold">
                            {language === 'es' ? 'Nombre' : 'Name'}
                          </th>
                          <th className="text-left p-3 text-brand-highlight font-semibold">
                            {language === 'es' ? 'Entrada' : 'Sign In'}
                          </th>
                          <th className="text-left p-3 text-brand-highlight font-semibold">
                            {language === 'es' ? 'Salida' : 'Sign Out'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {afterSchoolChildren.map((signin, idx) => (
                          <tr key={signin.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-surface-bg/50'}>
                            <td className="p-3 font-medium text-ink-main">{signin.childName}</td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.dropOffTime || '-'}</div>
                              {signin.dropOffPerson && <div className="text-xs text-ink-muted/70">{signin.dropOffPerson}</div>}
                            </td>
                            <td className="p-3 text-ink-muted">
                              <div>{signin.pickUpTime || '-'}</div>
                              {signin.pickUpPerson && <div className="text-xs text-ink-muted/70">{signin.pickUpPerson}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyListTab;
